import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(email, password);
      navigate(u.mustChangePassword ? '/change-password' : next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login">
      <div className="login__panel">
        <div className="login__brand">
          <img src="/design-system/assets/VIN-light.svg" alt="VIN" />
          <span className="login__brand-tag">AI Software Registry</span>
        </div>
        <div className="login__hello">For VIN Employees</div>
        <h1 className="login__headline">Approve, track, and request AI tools — together.</h1>
        <p className="login__lede">
          Submit any AI or software tool you'd like to use at work. IT and Legal review every
          request, and you can see exactly where it is at every step. No black boxes.
        </p>
        <ul className="login__bullets">
          <li>Lightweight wizard — about three minutes to submit.</li>
          <li>Transparent timeline with timestamps and reviewer comments.</li>
          <li>Searchable registry of every tool already approved.</li>
        </ul>
      </div>

      <div className="login__form-side">
        <div className="login__card">
          <h2 className="login__title">Sign in</h2>
          <p className="login__sub">Use your work email and password.</p>

          <form className="login__fields" onSubmit={(e) => void onSubmit(e)}>
            <div className="field">
              <label className="field__label" htmlFor="email">
                Work email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                autoComplete="username"
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="field__error">{error}</div>}
            <button
              type="submit"
              className="btn btn--primary btn--block btn--lg"
              disabled={submitting}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
            <div
              style={{
                fontSize: 12,
                textAlign: 'center',
                marginTop: 12,
                color: 'var(--color-steel)',
              }}
            >
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </form>

          <p
            style={{
              fontSize: 11,
              color: 'var(--color-steel)',
              textAlign: 'center',
              marginTop: 22,
              lineHeight: 1.5,
            }}
          >
            Internal tool — VIN staff only.
          </p>
        </div>
      </div>
    </div>
  );
}
